import React, { useState, useEffect, useRef } from 'react';
import { Box, Dialog, DialogTitle, DialogContent, Typography, Button, IconButton, DialogActions, Grid, Card, CardContent, CardActions } from '@mui/material';
import { authManager } from '@df/api';
import {
  buildLanternHandoffUrl,
  getPortalHandoff,
  getSafeReturnToFromSearch,
} from '../../utils/lanternHandoff';

import HeaderActions from './HeaderActions';
import LeftDrawer from './LeftDrawer';
// 3D components removed - will be replaced with Portal-specific components
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

export const shouldBypassPortalUi = ({ search, accessToken }) => (
  Boolean(accessToken) && Boolean(getPortalHandoff(search))
);

export const resolvePortalHandoffTarget = ({ search, lanternBase, accessToken, refreshToken }) => {
  const handoff = getPortalHandoff(search);
  if (!handoff) {
    return null;
  }

  return buildLanternHandoffUrl({
    lanternBase,
    handoff,
    accessToken,
    refreshToken,
    returnTo: getSafeReturnToFromSearch(search),
  });
};

// =======================================================
//  Component Begin
// =======================================================
const Portal = () => {
  const resolveLanternBase = () => {
    const rawBase = process.env.REACT_APP_PUBLIC_LANTERN_V3_BASE || '';
    if (rawBase) {
      return rawBase.replace(/\/+$/, '');
    }
    if (process.env.NODE_ENV === 'production') {
      return null;
    }
    return 'http://localhost:3000';
  };

  const handoff = getPortalHandoff(window.location.search);
  const isActiveHandoff = Boolean(handoff);
  const hasAccessToken = !!authManager.getAccessToken();

  // [NAV-DIAG] Token persistence check on page show
  useEffect(() => {
    return undefined;
  }, []);

  // Simple notification state for Portal
  const [notification, setNotification] = useState(null);

  const showSuccess = (message) => {
    setNotification({ type: 'success', message });
    setTimeout(() => setNotification(null), 3000);
  };

  const showError = (message) => {
    setNotification({ type: 'error', message });
    setTimeout(() => setNotification(null), 3000);
  };

  const showInfo = (message) => {
    setNotification({ type: 'info', message });
    setTimeout(() => setNotification(null), 3000);
  };

  // Auto-handoff logic for Cloud Space / Data Hub (run once, after tokens are ready)
  useEffect(() => {
    if (handoff !== 'cloud' && handoff !== 'datahub' && handoff !== 'lantern-v3') {
      return;
    }

    const accessToken = authManager.getAccessToken();
    if (!accessToken) {
      return;
    }

    const lanternBase = resolveLanternBase();
    if (!lanternBase) {
      showError('Lantern base URL is not configured.');
      return;
    }
    const refreshToken = authManager.getRefreshToken();
    const targetUrl = resolvePortalHandoffTarget({
      search: window.location.search,
      lanternBase,
      accessToken,
      refreshToken,
    });

    if (!targetUrl) {
      showError('Lantern handoff target is invalid.');
      return;
    }

    window.history.replaceState({}, '', '/portal');
    window.location.replace(targetUrl);
  }, []);

  // Simplified state management for Portal
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [openWorkPlace, setOpenWorkPlace] = useState(false);
  const [isLayerOpen, setIsLayerOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [path, setPath] = useState('');
  const [selectedFile, setSelectedFile] = useState(0);
  const [files, setFiles] = useState([]);
  const [activeSection, setActiveSection] = useState('Home');
  const [mlStudioOpen, setMlStudioOpen] = useState(false);
  const [unsupportedFileOpen, setUnsupportedFileOpen] = useState(false);
  const [fileTooLargeOpen, setFileTooLargeOpen] = useState(false);

  // File management - store files without processing
  const handleFileUpload = async (input, skipAddToList = false) => {
    const file = input.target.files[0];
    if (file) {
      const newFile = {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        id: Date.now() + Math.random() // Simple unique ID
      };
      
      if (!skipAddToList) {
        setFiles(prev => [...prev, newFile]);
      }
      
      showSuccess(`File "${file.name}" uploaded successfully`);
    }
  };

  const handleFileDelete = (fileId) => {
    setFiles(prev => prev.filter(file => file.id !== fileId));
    showSuccess('File deleted successfully');
  };

  const openWorkPlaceHandler = () => {
    setOpenWorkPlace(!openWorkPlace);
  };

  const LayerWindowHandler = () => {
    setIsLayerOpen(!isLayerOpen);
  };

  const saveModalOpener = (savingPath) => {
    setPath(savingPath);
    setIsSaveModalOpen(true);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('user_email');
    showSuccess('Logged out successfully');
  };

  const handleOpenSettings = () => {
    showInfo('Account settings are coming soon.');
  };

  // Navigation handlers for cards
  const buildHandoffUrl = (baseUrl, path) => {
    const accessToken = authManager.getAccessToken();
    if (!accessToken) return null;
    const refreshToken = authManager.getRefreshToken();
    const fragmentPayload = {
      a: accessToken,
      r: refreshToken || null,
      ts: Date.now()
    };
    const fragment = `#df8=${btoa(JSON.stringify(fragmentPayload))}`;
    return `${baseUrl}${path}${fragment}`;
  };

  const handleNavigateToLantern = () => {
    const lanternAppBase = resolveLanternBase();
    if (!lanternAppBase) {
      showError('Lantern base URL is not configured.');
      return;
    }
    const handoffUrl = buildHandoffUrl(lanternAppBase, '/');
    const lanternTargetUrl = handoffUrl || `${lanternAppBase}/`;
    if (handoffUrl) {
      window.location.replace(lanternTargetUrl);
      return;
    }
    window.location.assign(lanternTargetUrl);
  };

  const handleNavigateToCloudSpace = () => {
    const lanternBase = resolveLanternBase();
    if (!lanternBase) {
      showError('Lantern base URL is not configured.');
      return;
    }
    const handoffUrl = buildHandoffUrl(lanternBase, '/datahub');
    const targetUrl = handoffUrl || `${lanternBase}/datahub`;
    if (handoffUrl) {
      window.location.replace(targetUrl);
      return;
    }
    window.location.assign(targetUrl);
  };

  const handleOpenSampleDataset = () => {
    // TODO: Demo point cloud asset is pending - wire to actual demo file when available
    const lanternAppBase = resolveLanternBase();
    if (!lanternAppBase) {
      showError('Lantern base URL is not configured.');
      return;
    }
    const handoffUrl = buildHandoffUrl(lanternAppBase, '/?demo=1');
    const targetUrl = handoffUrl || `${lanternAppBase}/?demo=1`;
    if (handoffUrl) {
      window.location.replace(targetUrl);
      return;
    }
    window.location.assign(targetUrl);
  };

  // Placeholder handlers for compatibility
  const handleTransparencyChange = () => {};
  const handlePointSizeChange = () => {};
  const handleColorChange = () => {};
  const handleFeatureChange = () => {};
  const handleCoordinateAxisChange = () => {};
  const handlePaletteChange = () => {};
  const handleTransferFunctionChange = () => {};
  const handleApplyPreset = () => {};
  const handleDarkModeToggle = () => {};
  const toggleSelectActive = () => {};
  const handleDownsampleRatioChange = () => {};
  const handleSizeModeChange = () => {};
  const handlePerformanceChange = () => {};
  const handleShapeChange = () => {};
  const handleAdvancedChange = () => {};
  const handleLightingChange = () => {};
  const handleCameraChange = () => {};
  const handleViewStateChange = () => {};
  const handleBoundingBoxHelperReady = () => {};
  const handleSceneRendererCapture = () => {};

  if (isActiveHandoff && hasAccessToken) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          textAlign: 'center',
          backgroundColor: '#ffffff',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Redirecting…
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Taking you to Lantern.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        overflowX: 'hidden',
        overflowY: 'hidden',
        width: '100%',
        Height: '100vh',
      }}
    >
      <HeaderActions
        isAuthenticated={isAuthenticated}
        onLogout={handleLogout}
        userEmail={localStorage.getItem('user_email') || ''}
        onOpenSettings={handleOpenSettings}
      />

      {/* Simple Notification */}
      {notification && (
        <Box
          sx={{
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 9999,
            backgroundColor: notification.type === 'success' ? '#4caf50' : 
                           notification.type === 'error' ? '#f44336' : '#2196f3',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '4px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            maxWidth: '300px',
            wordWrap: 'break-word'
          }}
        >
          {notification.message}
        </Box>
      )}

      <Box
        sx={{
          width: '100%',
          display: 'flex', flexDirection: 'row',
          alignItems: 'center', justifyContent: 'center',
          zIndex: 50,
          height: '88vh',
        }}
      >
        {/* 3D components removed - Portal content will be added here */}

        <LeftDrawer
          isAuthenticated={isAuthenticated}
          setIsAuthenticated={setIsAuthenticated}
          onFileUpload={handleFileUpload}
          uploadedFiles={files}
          dataIndexToSaveHandler={(a) => setSelectedFile(a)}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          buildHandoffUrl={buildHandoffUrl}
          onOpenMLStudio={() => setMlStudioOpen(true)}
          onMissingLanternBase={() => showError('Lantern base URL is not configured.')}
        />

        {/* Main Home Content */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            overflowY: 'auto',
            padding: '2rem',
          }}
        >
          {/* Cards Grid */}
          <Box
            sx={{
              flex: 1,
              maxWidth: '1200px',
              width: '100%',
              margin: '0 auto',
            }}
          >
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* Primary CTA Card - Lantern Viewer */}
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '2px solid var(--color-primary-600)',
                    boxShadow: '0 4px 12px rgba(45, 94, 255, 0.15)',
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'var(--color-primary-600)' }}>
                      Lantern Viewer
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Launch the 3D/4D workspace to visualize, explore, and measure your data.
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleNavigateToLantern}
                      sx={{
                        backgroundColor: 'var(--color-primary-600)',
                        '&:hover': {
                          backgroundColor: 'var(--color-primary-600)',
                        },
                      }}
                    >
                      Go to Lantern
                    </Button>
                  </CardActions>
                </Card>
              </Grid>

              {/* Cloud Space Card */}
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                      My Cloud Space
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Upload new datasets or manage your existing files before opening them in Lantern.
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={handleNavigateToCloudSpace}
                    >
                      Open Cloud Space
                    </Button>
                  </CardActions>
                </Card>
              </Grid>

              {/* Sample Dataset Card */}
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                      Try a Sample Dataset
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Load a demo point cloud to see how Lantern works in seconds.
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={handleOpenSampleDataset}
                    >
                      Open Sample Dataset
                    </Button>
                  </CardActions>
                </Card>
              </Grid>

              {/* Quick Start Card */}
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                      Quick Start
                    </Typography>
                    <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 1 }}>
                      <ol style={{ paddingLeft: '1.25rem', margin: 0, lineHeight: '1.8' }}>
                        <li>Upload a dataset in My Cloud Space</li>
                        <li>Open it in Lantern</li>
                        <li>Explore it in 3D and export results</li>
                      </ol>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, fontStyle: 'italic' }}>
                      More tutorials coming soon.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Footer Strip */}
          <Box
            sx={{
              display: 'flex',
                justifyContent: 'space-between',
              alignItems: 'center',
                padding: '1rem 0',
                borderTop: '1px solid var(--color-border-200)',
                mt: 'auto',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                Lantern is in Beta — Some modules are still under development.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                <Button
                  variant="text"
                  size="small"
                  href="mailto:connect@datafre8.com"
                  sx={{
                    textTransform: 'none',
                    color: 'var(--color-primary-600)',
                  }}
                >
                  Send Feedback
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Report a bug or tell us what you need next.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Dialog open={mlStudioOpen} onClose={() => setMlStudioOpen(false)} fullScreen>
        <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: '1px solid #eee', minHeight: 64 }}>
          <IconButton onClick={() => setMlStudioOpen(false)} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            ML Studio
          </Typography>
        </Box>
        {/* Modal content goes here */}
      </Dialog>

      <Dialog open={unsupportedFileOpen} onClose={() => setUnsupportedFileOpen(false)}>
        <DialogTitle>Unsupported File Type</DialogTitle>
        <DialogContent>
          This file type is not supported by Portal. Please upload it to your cloud workspace to convert to a standard format.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnsupportedFileOpen(false)} color="primary">OK</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={fileTooLargeOpen} onClose={() => setFileTooLargeOpen(false)}>
        <DialogTitle>File Too Large</DialogTitle>
        <DialogContent>
          This file is too large for local viewing. Please upload it to your cloud workspace for processing.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFileTooLargeOpen(false)} color="primary">OK</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Portal; 
